import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ModalDirective, ModalModule} from 'ngx-bootstrap/modal';
import {Subscription} from 'rxjs';
import {ToasterService} from 'angular2-toaster';
import {SessionExpirationService} from '../services/sessionExpiration.service';
import {InitializationAndConfigurationService} from '../services/configuration/initializationAndConfiguration.service';

/**
 * Modal shown when the user is approaching the idle timeout. Counts down the
 * remaining seconds; clicking "Stay logged in" (or any activity in this or any
 * sibling tab) cancels the warning.
 */
@Component({
    selector: 'app-session-warning-modal',
    standalone: true,
    imports: [CommonModule, ModalModule],
    template: `
        <div bsModal #sessionWarningModal="bs-modal"
             [config]="{backdrop: 'static', keyboard: false}"
             class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <b>Inactive Session Warning</b>
                    </div>
                    <div class="modal-body modal-body-touch">
                        <div>{{ warningMessage }}</div>
                        <br>
                        <div class="text-center">
                            You will be logged out automatically in:
                            <h1>{{ formatCountdown(secondsRemaining) }}</h1>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-primary" (click)="stayLoggedIn()">
                            Stay logged in
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class SessionWarningModalComponent implements OnInit, OnDestroy {
    @ViewChild('sessionWarningModal', {static: true}) modal: ModalDirective;

    secondsRemaining = 0;
    warningMessage = 'Your session is about to expire. Click around in the window to stay logged in.';

    private subs: Subscription[] = [];
    private keptAliveMessages: string[] = [];

    constructor(private sessionExpirationService: SessionExpirationService,
                private toasterService: ToasterService,
                private initializationAndConfigurationService: InitializationAndConfigurationService) {
    }

    ngOnInit(): void {
        const config = this.initializationAndConfigurationService.getApplicationConfiguration();
        this.keptAliveMessages = (config && config.sessionKeptAliveMessagesArray) || [];

        this.subs.push(this.sessionExpirationService.warningVisible$.subscribe(visible => {
            if (visible) {
                this.modal.show();
            } else {
                this.modal.hide();
            }
        }));

        this.subs.push(this.sessionExpirationService.secondsRemaining$.subscribe(seconds => {
            this.secondsRemaining = seconds;
        }));

        this.subs.push(this.sessionExpirationService.keptAlive$.subscribe(() => {
            const message = this.pickKeptAliveMessage();
            if (message) {
                this.toasterService.pop('info', message);
            }
        }));
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    stayLoggedIn(): void {
        this.sessionExpirationService.stayLoggedIn();
    }

    formatCountdown(seconds: number): string {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
    }

    private pickKeptAliveMessage(): string | null {
        if (!this.keptAliveMessages.length) {
            return null;
        }
        return this.keptAliveMessages[Math.floor(Math.random() * this.keptAliveMessages.length)];
    }
}
